import {
  getAppField,
  formatRating,
  getEmploymentHistoryEntries,
  getExperienceSummary,
} from './applicationFields';
import { buildAttachmentUrl, buildAttachmentViewerPageUrl } from './attachmentViewer';
import { HELPORT_MARK_SVG, getHelportMarkFaviconDataUri } from '../components/HelportMarkIcon';

const escapeHtml = (value) => (
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
);

const val = (value, fallback = 'N/A') => {
  const text = String(value ?? '').trim();
  return text && text !== 'N/A' ? text : fallback;
};

const fieldHtml = (label, value, wide = false) => `
  <div class="field${wide ? ' field--wide' : ''}">
    <span class="field-label">${escapeHtml(label)}</span>
    <span class="field-value">${escapeHtml(val(value))}</span>
  </div>
`;

const sectionHtml = (title, body, wide = false) => `
  <section class="section${wide ? ' section--wide' : ''}">
    <h2>${escapeHtml(title)}</h2>
    <div class="section-body">${body}</div>
  </section>
`;

const PAGE_STYLES = `
  *{box-sizing:border-box}
  body{margin:0;background:#0b1220;color:#e2e8f0;font-family:Segoe UI,Arial,sans-serif;line-height:1.5}
  .viewer-header{display:flex;align-items:center;gap:12px;padding:14px 20px;background:#060912;border-bottom:1px solid rgba(0,210,127,.28);position:sticky;top:0;z-index:5}
  .viewer-header__mark{display:inline-flex;flex-shrink:0}
  .viewer-header__copy{display:flex;flex-direction:column;gap:2px;min-width:0;flex:1}
  .viewer-header__title{font-size:.95rem;color:#00d27f;font-weight:700;letter-spacing:.04em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .viewer-header__sub{font-size:.72rem;color:#94a3b8}
  .viewer-close-btn{flex-shrink:0;width:28px;height:28px;min-width:28px;min-height:28px;padding:0;display:inline-flex;align-items:center;justify-content:center;border-radius:7px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.06);color:#c8d0dc;font-size:1rem;line-height:1;cursor:pointer;transition:background .15s ease,border-color .15s ease,color .15s ease}
  .viewer-close-btn:hover,.viewer-close-btn:focus-visible{background:rgba(255,77,79,.14);border-color:rgba(255,77,79,.4);color:#ff8e8e;outline:none}
  .viewer-main{max-width:980px;margin:0 auto;padding:20px 16px 40px}
  .resume-hero,.details-hero{padding:18px 20px;border:1px solid rgba(148,163,184,.16);border-radius:12px;background:rgba(15,23,42,.72);margin-bottom:16px}
  .resume-hero{text-align:center}
  .resume-hero h1,.details-hero h1{margin:0 0 8px;font-size:1.45rem;color:#f8fafc}
  .resume-hero p,.details-hero p{margin:0;color:#94a3b8;font-size:.9rem}
  .resume-hero__meta{display:inline-flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:8px 0;max-width:100%}
  .resume-hero__meta-sep{margin:0 10px;color:#64748b;font-weight:500}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
  .section{border:1px solid rgba(148,163,184,.14);border-radius:12px;background:rgba(15,23,42,.55);padding:14px 16px;border-left:3px solid #00d27f}
  .section--wide{grid-column:1/-1}
  .section h2{margin:0 0 12px;padding-bottom:8px;border-bottom:1px solid rgba(148,163,184,.12);color:#4ade80;font-size:.78rem;letter-spacing:.1em;text-transform:uppercase}
  .section-body{display:grid;grid-template-columns:1fr 1fr;gap:10px 14px}
  .field{display:flex;flex-direction:column;gap:3px;min-width:0}
  .field--wide{grid-column:1/-1}
  .field-label{font-size:.68rem;letter-spacing:.06em;text-transform:uppercase;color:#64748b;font-weight:600}
  .field-value{font-size:.9rem;color:#e2e8f0;word-break:break-word}
  .attach-list{display:flex;flex-direction:column;gap:8px}
  .attach-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;border-radius:8px;background:rgba(148,163,184,.06);border:1px solid rgba(148,163,184,.1)}
  .attach-row a{color:#38bdf8;font-weight:600;text-decoration:none;font-size:.82rem}
  .attach-row a:hover{text-decoration:underline}
  .emp-card{padding:10px 12px;border-radius:8px;background:rgba(148,163,184,.05);border:1px solid rgba(148,163,184,.1);margin-bottom:8px}
  .emp-card h3{margin:0 0 6px;font-size:.85rem;color:#cbd5e1}
  .muted{color:#94a3b8;font-size:.85rem}
  @media (max-width:720px){
    .grid,.section-body{grid-template-columns:1fr}
    .viewer-header{padding:12px 14px;gap:10px}
    .viewer-header__title{font-size:.84rem}
    .viewer-close-btn{width:30px;height:30px;min-width:30px;min-height:30px;font-size:1.05rem}
  }
`;

const CLOSE_SCRIPT = `
  (function(){
    function returnToReview(){
      try{
        if(window.opener && !window.opener.closed){
          window.opener.focus();
        }
      }catch(e){}
      window.close();
    }
    var btn=document.getElementById('viewer-close-btn');
    if(btn){btn.addEventListener('click',returnToReview);}
    document.addEventListener('keydown',function(e){
      if(e.key==='Escape'){returnToReview();}
    });
  })();
`;

const getViewerFaviconHref = () => {
  const publicUrl = String(process.env.PUBLIC_URL || '').replace(/\/$/, '');
  try {
    return new URL(`${publicUrl}/favicon.png`, window.location.origin).href;
  } catch {
    return `${window.location.origin}${publicUrl}/favicon.png`;
  }
};

const openHtmlWindow = (title, bodyHtml) => {
  const safeTitle = escapeHtml(title);
  const faviconPng = escapeHtml(getViewerFaviconHref());
  const faviconSvg = escapeHtml(getHelportMarkFaviconDataUri());
  const html = [
    '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width,initial-scale=1">',
    `<title>${safeTitle}</title>`,
    `<link rel="icon" type="image/png" href="${faviconPng}">`,
    `<link rel="shortcut icon" type="image/png" href="${faviconPng}">`,
    `<link rel="alternate icon" type="image/svg+xml" href="${faviconSvg}">`,
    `<style>${PAGE_STYLES}</style></head><body>`,
    '<header class="viewer-header">',
    `<span class="viewer-header__mark" aria-hidden="true">${HELPORT_MARK_SVG}</span>`,
    '<div class="viewer-header__copy">',
    `<span class="viewer-header__title">${safeTitle}</span>`,
    '<span class="viewer-header__sub">Back to Applicants for Review</span>',
    '</div>',
    '<button type="button" id="viewer-close-btn" class="viewer-close-btn" aria-label="Close and return to Applicants for Review" title="Close">&times;</button>',
    '</header>',
    `<main class="viewer-main">${bodyHtml}</main>`,
    '<script>',
    CLOSE_SCRIPT,
    '</script></body></html>',
  ].join('');

  try {
    // Blob URL (not about:blank) so the browser reliably applies title + Helport favicon.
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const viewer = window.open(url, '_blank');
    if (!viewer) {
      URL.revokeObjectURL(url);
      return false;
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 120000);
    return true;
  } catch {
    return false;
  }
};

const buildEmploymentHtml = (app) => {
  const entries = getEmploymentHistoryEntries(app);
  const summary = getExperienceSummary(app);

  if (!entries.length && !summary) {
    return `<p class="muted">No employment history provided.</p>`;
  }

  const summaryHtml = summary
    ? `<p class="muted"><strong>Total experience:</strong> ${escapeHtml(summary)}</p>`
    : '';

  const cards = entries.map((entry) => `
    <article class="emp-card">
      <h3>${escapeHtml(entry.label)}</h3>
      ${fieldHtml('Company / Role', entry.company, true)}
      ${entry.reason ? fieldHtml('Reason for Leaving', entry.reason, true) : ''}
      ${entry.clearance ? fieldHtml('Clearance / COE', entry.clearance, true) : ''}
    </article>
  `).join('');

  return `${summaryHtml}${cards}`;
};

const applicantDisplayName = (app) => {
  const name = `${app?.first_name || ''} ${app?.last_name || ''}`.trim();
  return name || 'Applicant';
};

export const openDigitalResumeTab = (app, labels = {}) => {
  if (!app) return false;

  const sections = labels.sections || {};
  const name = applicantDisplayName(app);
  const title = labels.viewResume || 'Digital Resume';

  const body = `
    <div class="resume-hero">
      <h1>${escapeHtml(name)}</h1>
      <p class="resume-hero__meta">
        <span>Email: ${escapeHtml(val(app.email))}</span>
        <span class="resume-hero__meta-sep" aria-hidden="true">|</span>
        <span>Mobile No.: ${escapeHtml(val(app.mobile_number))}</span>
        <span class="resume-hero__meta-sep" aria-hidden="true">|</span>
        <span>City or Municipality: ${escapeHtml(val(app.city_municipality))}</span>
      </p>
    </div>
    <div class="grid">
      ${sectionHtml(sections.profExperience || 'Professional Experience', `
        ${fieldHtml('BPO Experience', app.bpo_experience)}
        ${fieldHtml('Latest Company', app.latest_company || 'None')}
        ${fieldHtml('Total Years Working', app.total_years_work || 'None')}
        ${fieldHtml('Relevant Years', app.relevant_years_work || 'None')}
        ${fieldHtml('Reason for Leaving', app.reason_for_leaving, true)}
        <div class="field field--wide">${buildEmploymentHtml(app)}</div>
      `, true)}
      ${sectionHtml(sections.eduBackground || 'Educational Background', `
        ${fieldHtml('Highest Attainment', app.highest_educ, true)}
        ${fieldHtml('School Name', app.school_name, true)}
        ${fieldHtml('Year Graduated', app.year_graduated)}
      `, true)}
      ${sectionHtml(sections.personalInfo || 'Personal Information', `
        ${fieldHtml('Date of Birth', getAppField(app, ['dob', 'dateOfBirth']))}
        ${fieldHtml('Gender', app.gender)}
        ${fieldHtml('Civil Status', app.civil_status)}
        ${fieldHtml('Nationality', app.nationality)}
        ${fieldHtml('Current Address', app.current_address, true)}
      `, true)}
      ${sectionHtml(sections.emergency || 'Emergency Contact', `
        ${fieldHtml('Contact Name', app.emergency_contact_name)}
        ${fieldHtml('Relationship', app.emergency_contact_relation)}
        ${fieldHtml('Contact Number', app.emergency_contact_number)}
      `, true)}
    </div>
  `;

  return openHtmlWindow(`${title} — ${name}`, body);
};

export const openFullDetailsTab = (app, options = {}) => {
  if (!app) return false;

  const {
    labels = {},
    attachmentDocs = [],
    assessmentRecord = null,
    canSeeScore = false,
  } = options;

  const sections = labels.sections || {};
  const attachmentsLabel = labels.attachments || {};
  const name = applicantDisplayName(app);
  const title = labels.reviewDetails || 'Full Details';

  const attachmentRows = (attachmentDocs || []).map((doc) => {
    const file = getAppField(app, doc.keys, '');
    const hasFile = file && file !== 'N/A';
    const url = hasFile ? buildAttachmentUrl(file, doc.label) : '';
    const openUrl = url ? buildAttachmentViewerPageUrl(url, doc.label) : '';
    return `
      <div class="attach-row">
        <span>${escapeHtml(doc.label || attachmentsLabel.resume || 'Attachment')}</span>
        ${openUrl
          ? `<a href="${escapeHtml(openUrl)}" target="_blank" rel="noopener noreferrer">View</a>`
          : `<span class="muted">Not uploaded</span>`}
      </div>
    `;
  }).join('');

  let voiceHtml = '';
  if (canSeeScore) {
    if (!assessmentRecord) {
      voiceHtml = sectionHtml(labels.voiceScore || 'Voice Score', `<p class="muted">${escapeHtml(labels.noAssessmentYet || 'No assessment yet')}</p>`, true);
    } else {
      const exerciseScores = assessmentRecord.exercise_scores || {};
      voiceHtml = sectionHtml(labels.voiceScore || 'Voice Score', `
        ${fieldHtml(labels.overallScore || 'Overall Score', assessmentRecord.overall_score ?? '—')}
        ${fieldHtml(labels.englishConfidenceShort || 'English Confidence', `${assessmentRecord.english_confidence ?? '—'}/10`)}
        ${fieldHtml(labels.completedTrainingShort || 'Completed Training', assessmentRecord.completed_training === 'yes' ? 'Yes' : assessmentRecord.completed_training === 'no' ? 'No' : '—')}
        ${fieldHtml(labels.exGreeting || 'Greeting', exerciseScores.greeting ?? '—')}
        ${fieldHtml(labels.exEmpathy || 'Empathy', exerciseScores.empathy ?? '—')}
        ${fieldHtml(labels.exHold || 'Hold', exerciseScores.hold ?? '—')}
        ${fieldHtml(labels.exClosing || 'Closing', exerciseScores.closing ?? '—')}
        ${assessmentRecord.notes ? fieldHtml(labels.assessmentNotes || 'Notes', assessmentRecord.notes, true) : ''}
      `, true);
    }
  }

  const fullName = `${app.first_name || ''} ${app.middle_name || ''} ${app.last_name || ''} ${app.suffix || ''}`.trim();

  const body = `
    <div class="details-hero">
      <h1>${escapeHtml(name)}</h1>
      <p>${escapeHtml(val(app.position))} · ${escapeHtml(val(app.status))}</p>
    </div>
    <section class="section section--wide">
      <h2>${escapeHtml(sections.attachments || 'Attachments')}</h2>
      <div class="attach-list">${attachmentRows || `<p class="muted">No attachments listed.</p>`}</div>
    </section>
    <div class="grid">
      ${sectionHtml(sections.personal || 'Personal', `
        ${fieldHtml('Full Name', fullName)}
        ${fieldHtml('Date of Birth', getAppField(app, ['dob', 'dateOfBirth', 'date_of_birth']))}
        ${fieldHtml('Gender', getAppField(app, ['gender']))}
        ${fieldHtml('Civil Status', getAppField(app, ['civil_status', 'civilStatus']))}
        ${fieldHtml('Nationality', getAppField(app, ['nationality']))}
        ${fieldHtml('Religion', getAppField(app, ['religion']))}
        ${fieldHtml('Is Minor', getAppField(app, ['is_minor', 'isMinor']))}
      `)}
      ${sectionHtml(sections.contact || 'Contact', `
        ${fieldHtml('Email', app.email)}
        ${fieldHtml('Mobile Number', app.mobile_number)}
        ${fieldHtml('Current Address', app.current_address, true)}
        ${fieldHtml('Permanent Address', app.permanent_address, true)}
        ${fieldHtml('Emergency Contact', `${val(app.emergency_contact_name)} (${val(app.emergency_contact_relation)}) — ${val(app.emergency_contact_number)}`, true)}
      `)}
      ${sectionHtml(sections.education || 'Education & Experience', `
        ${fieldHtml('Highest Educational Attainment', `${val(app.highest_educ)} at ${val(app.school_name)} (${val(app.year_graduated)})`, true)}
        ${fieldHtml('BPO Experience', app.bpo_experience)}
        ${fieldHtml('Latest Company Name', app.latest_company)}
        ${fieldHtml('Total Years of Experience', app.total_years_work)}
        ${fieldHtml('Total Relevant Experience', app.relevant_years_work)}
        ${fieldHtml('Reason for Leaving', app.reason_for_leaving, true)}
        <div class="field field--wide">${buildEmploymentHtml(app)}</div>
      `, true)}
      ${sectionHtml(sections.skills || 'Skills', `
        ${fieldHtml('English Oral Skills', formatRating(app, ['english_oral_proficiency', 'englishOralSkills']))}
        ${fieldHtml('English Written Skills', formatRating(app, ['english_written_proficiency', 'englishWrittenSkills']))}
        ${fieldHtml('Other Languages Known', `${getAppField(app, ['other_languages', 'otherLanguages'], 'None')} (Oral: ${getAppField(app, ['other_lang_oral_proficiency', 'otherLanguagesOralSkills'])} | Written: ${getAppField(app, ['other_lang_written_proficiency', 'otherLanguagesWrittenSkills'])})`, true)}
      `)}
      ${voiceHtml}
      ${sectionHtml(sections.application || 'Application', `
        ${fieldHtml('Position Applied', app.position)}
        ${fieldHtml('Current Stage / Status', app.status)}
        ${fieldHtml('City/Municipality', app.city_municipality)}
        ${fieldHtml('Date Applied', app.date_of_application)}
        ${fieldHtml('Source of Referral', app.source_referral)}
        ${fieldHtml('Referred By (Name)', app.referrer_name)}
        ${fieldHtml('Has Relative in Helport', `${val(app.has_relative_in_helport || app.has_realtive_in_helport, 'No')}${app.relative_name ? ` — ${app.relative_name} (${val(app.relative_relation)})` : ''}`, true)}
        ${fieldHtml('Transparency Notes / Accommodations', getAppField(app, ['transparency_notes', 'specialAccommodations'], 'None Specified'), true)}
      `)}
      ${sectionHtml(sections.government || 'Government IDs', `
        ${fieldHtml('TIN', getAppField(app, ['tin_number', 'tinNumber', 'tin']))}
        ${fieldHtml('SSS No.', getAppField(app, ['sss_number', 'sssNumber', 'sss']))}
        ${fieldHtml('PhilHealth No.', getAppField(app, ['philhealth_number', 'philHealthNumber', 'phic']))}
        ${fieldHtml('Pag-IBIG No.', getAppField(app, ['pagibig_number', 'pagIbigNumber', 'hdmf']))}
        ${fieldHtml('NBI Status', getAppField(app, ['nbi_status', 'nbiStatus']), true)}
      `)}
      ${sectionHtml(sections.employment || 'Employment', `
        ${fieldHtml('Currently Employed', getAppField(app, ['is_currently_employed', 'isCurrentlyEmployed']))}
        ${fieldHtml('Current Employer', getAppField(app, ['current_employer', 'currentEmployer']))}
        ${fieldHtml('Current Role', getAppField(app, ['current_role', 'currentRole']))}
        ${fieldHtml('Employment Period', getAppField(app, ['current_employment_period', 'currentEmploymentPeriod']))}
        ${fieldHtml('Notice Period', getAppField(app, ['notice_period', 'noticePeriod']))}
        ${fieldHtml('Outstanding Job Offer', getAppField(app, ['has_outstanding_job_offer', 'hasOutstandingJobOffer']))}
        ${fieldHtml('Background Check Consent', getAppField(app, ['comprehensive_background_check', 'comprehensiveBackgroundCheck']), true)}
        ${fieldHtml('Drug-Free Workplace', getAppField(app, ['drug_free_workplace', 'drugFreeWorkplace']), true)}
        ${fieldHtml('Past Infractions', getAppField(app, ['past_infractions_status', 'pastInfractionsStatus']), true)}
      `, true)}
      ${sectionHtml(sections.workSetup || 'Work Setup', `
        ${fieldHtml('Work Setup', getAppField(app, ['work_setup', 'workSetup']))}
        ${fieldHtml('Shifting Schedule', getAppField(app, ['shifting_schedule', 'shiftingSchedule']))}
        ${fieldHtml('Work Schedule', getAppField(app, ['work_schedule', 'workSchedule']))}
        ${fieldHtml('Health Status', getAppField(app, ['current_health_status', 'currentHealthStatus']), true)}
        ${fieldHtml('Applicant Declaration', getAppField(app, ['applicant_declaration_agreement', 'applicantDeclarationAgreement']), true)}
        ${fieldHtml('Data Privacy Consent', getAppField(app, ['data_privacy_consent', 'dataPrivacyConsent']), true)}
      `)}
    </div>
  `;

  return openHtmlWindow(`${title} — ${name}`, body);
};
